import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchUserGaugeData, fetchUserTokenBalance, fetchPoolMetrics, fetchUserSlipstreamPositions, fetchTokenTotalSupply, fetchNftPositionDetails, fetchPoolSugarData, fetchTokenPrice, formatFromWei, calculatePositionValueUsd } from '../api/userPositions';
import {
  formatTickRange,
  isInRange,
  getFeeTierDescription,
  calculateNftPositionValueUsd,
  getPoolByAddress,
  getTokenByAddress,
  POOLS
} from '@sepori/shared';
import type { UserPosition, UserPositionGroup } from '../api/types';

export function useUserPositions(walletAddress: string | null) {
  const [positionGroups, setPositionGroups] = useState<UserPositionGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Get all pool entries (memoized to prevent re-renders)
  const poolEntries = useMemo(() => Object.entries(POOLS), []);

  const processUserPositions = useCallback(async (walletAddr: string, bypassCache = false): Promise<UserPositionGroup[]> => {
    if (import.meta.env.DEV) console.log('🔍 Starting position detection for wallet:', walletAddr);
    const groups: UserPositionGroup[] = [
      { type: 'slipstream', positions: [] },
      { type: 'basic', positions: [] }
    ];

    // Validate wallet address
    if (!walletAddr || walletAddr.length !== 42 || !walletAddr.startsWith('0x')) {
      throw new Error(`Invalid wallet address: ${walletAddr}`);
    }

    // Fetch token prices once for all positions
    const WETH_ADDRESS = '0x4200000000000000000000000000000000000006'; // WETH on Base
    const AERO_ADDRESS = '0x940181a94a35a4569e4529a3cdfb74e38fd98631'; // AERO on Base

    let wethPrice = 0;
    let aeroPrice = 0;

    try {
      if (import.meta.env.DEV) console.log('🔍 Fetching token prices...');
      const [wethPriceData, aeroPriceData] = await Promise.all([
        fetchTokenPrice(WETH_ADDRESS, { bypassCache }),
        fetchTokenPrice(AERO_ADDRESS, { bypassCache })
      ]);

      if (import.meta.env.DEV) console.log('🔍 WETH price response:', wethPriceData);
      if (import.meta.env.DEV) console.log('🔍 AERO price response:', aeroPriceData);

      // Check if priceUsd field exists in response
      if (wethPriceData && typeof wethPriceData.priceUsd === 'number') {
        wethPrice = wethPriceData.priceUsd;
      } else {
        console.error('❌ WETH price data missing or invalid:', wethPriceData);
      }

      if (aeroPriceData && typeof aeroPriceData.priceUsd === 'number') {
        aeroPrice = aeroPriceData.priceUsd;
      } else {
        console.error('❌ AERO price data missing or invalid:', aeroPriceData);
      }

      if (import.meta.env.DEV) console.log(`📊 Token prices: WETH=$${wethPrice.toFixed(2)}, AERO=$${aeroPrice.toFixed(4)}`);

      if (wethPrice <= 0 || aeroPrice <= 0) {
        console.error('❌ Invalid token prices detected:', { wethPrice, aeroPrice });
      }
    } catch (priceErr) {
      console.error('❌ Failed to fetch token prices:', priceErr);
      // Continue without prices but log the error
    }

    // Debug data collection
    const debugUserPositions: any[] = [];

    // Process all pools in parallel using shared registry
    const poolEntries = Object.values(POOLS);
    const positionPromises = poolEntries.map(async (pool: any) => {
      try {
        if (import.meta.env.DEV) console.log(`🔍 Checking pool: ${pool.name} (${pool.pool})`);
        if (import.meta.env.DEV) console.log(`🔍 Gauge: ${pool.gauge}`);

        // For slipstream pools, use NFT-based position detection
        if (pool.kind === 'slipstream') {
          try {
            if (import.meta.env.DEV) console.log(`🔍 Checking slipstream NFT positions for ${pool.name}`);
            const nftData = await fetchUserSlipstreamPositions(pool.gauge, walletAddr);
            if (import.meta.env.DEV) console.log(`📊 NFT data for ${pool.name}:`, nftData);

            // Collect debug data for this pool
            const debugPosition = {
              poolName: pool.name,
              poolAddress: pool.pool,
              gaugeAddress: pool.gauge,
              nftPositions: nftData.tokenIds.map((tokenId: string) => ({
                tokenId,
                earned: nftData.rewards[tokenId] || '0',
                earnedFormatted: formatFromWei(nftData.rewards[tokenId] || '0', 18)
              })),
              totalNfts: nftData.totalNfts,
              hasPosition: nftData.totalNfts > 0,
              errors: nftData.totalNfts === 0 ? ['No staked NFT positions found'] : [],
            };
            debugUserPositions.push(debugPosition);

                    if (nftData.totalNfts > 0) {
                      // Fetch pool metrics for APR and sugar data for current tick (once for all NFTs)
                      // Use Promise.allSettled to handle individual failures gracefully
                      const [poolMetricsResult, sugarDataResult] = await Promise.allSettled([
                        fetchPoolMetrics(pool.pool, { bypassCache }),
                        fetchPoolSugarData(pool.pool, { bypassCache })
                      ]);

                      // Extract results or use fallback values
                      let poolMetrics: any = null;
                      let sugarData: any = null;

                      if (poolMetricsResult.status === 'fulfilled') {
                        poolMetrics = poolMetricsResult.value;
                      } else {
                        console.error(`❌ fetchPoolMetrics failed for ${pool.name} (${pool.pool}):`, poolMetricsResult.reason);
                        // Use fallback values
                        poolMetrics = { tvlUsd: 0, stakedUsd: 0, aprCurrent: 0, aprCurrentPct: 0 };
                      }

                      if (sugarDataResult.status === 'fulfilled') {
                        sugarData = sugarDataResult.value;
                      } else {
                        console.error(`❌ fetchPoolSugarData failed for ${pool.name} (${pool.pool}):`, sugarDataResult.reason);
                        // Use fallback values
                        sugarData = { kind: "slipstream_sugar", tick: 0, emissions: '0', gauge_liquidity: '0' };
                      }

                      if (import.meta.env.DEV) console.log(`📊 Pool metrics for ${pool.name}:`, poolMetrics);
                      if (import.meta.env.DEV) console.log(`📊 Sugar data for ${pool.name}:`, sugarData);

                      // Get current tick from sugar data
                      const currentTick = sugarData.tick || 0;
                      const emissions = sugarData.emissions || '0';
                      const gaugeLiquidity = sugarData.gauge_liquidity || '0';
                      if (import.meta.env.DEV) console.log(`📊 Current tick for ${pool.name}: ${currentTick}`);
                      if (import.meta.env.DEV) console.log(`📊 Emissions for ${pool.name}: ${emissions} wei/sec`);
                      if (import.meta.env.DEV) console.log(`📊 Gauge liquidity for ${pool.name}: ${gaugeLiquidity}`);

                      // Create a position for each NFT and return them
                      const nftPositions = await Promise.all(nftData.tokenIds.map(async (tokenId: string) => {
                        const claimableEmissions = formatFromWei(nftData.rewards[tokenId] || '0', 18);

                        // Try to fetch NFT position details
                        let positionDetails = null;
                        let priceRange = 'Unknown';
                        let isInRangeFlag = false;
                        let feeTier = 'Unknown';

                        try {
                          if (import.meta.env.DEV) console.log(`🔍 Fetching NFT position details for tokenId ${tokenId}`);
                          positionDetails = await fetchNftPositionDetails(tokenId);

                          // Get token decimals from position details
                          const token0Decimals = 18; // WETH typically has 18 decimals
                          const token1Decimals = 6;  // USDC typically has 6 decimals

                          // Format price range with proper decimal handling
                          priceRange = formatTickRange(
                            positionDetails.tickLower,
                            positionDetails.tickUpper,
                            'WETH', // token0 symbol - could be made dynamic
                            'USDC', // token1 symbol - could be made dynamic
                            token0Decimals,
                            token1Decimals
                          );

                          // Check if in range using current tick from sugar data
                          isInRangeFlag = isInRange(currentTick, positionDetails.tickLower, positionDetails.tickUpper);

                          // Get fee tier description
                          feeTier = getFeeTierDescription(positionDetails.fee);

                          if (import.meta.env.DEV) console.log(`📊 NFT position details for ${tokenId}:`, {
                            tickLower: positionDetails.tickLower,
                            tickUpper: positionDetails.tickUpper,
                            currentTick,
                            priceRange,
                            isInRange: isInRangeFlag,
                            fee: positionDetails.fee,
                            feeTier,
                            liquidity: positionDetails.liquidity
                          });
                        } catch (nftErr) {
                          if (import.meta.env.DEV) console.log(`⚠️ Failed to fetch NFT details for ${tokenId}:`, nftErr);
                        }

                        // Calculate actual USD value from NFT liquidity using registry metadata
                        let positionValueUsd = 0;
                        if (positionDetails && wethPrice > 0) {
                          // Get pool metadata to determine token addresses and decimals
                          const poolMeta = getPoolByAddress(pool.pool);
                          if (!poolMeta) {
                            console.error(`❌ Pool metadata not found for ${pool.pool}`);
                          } else {
                            // Get token metadata for proper decimals
                            const token0Meta = getTokenByAddress(poolMeta.token0);
                            const token1Meta = getTokenByAddress(poolMeta.token1);

                            if (!token0Meta || !token1Meta) {
                              console.error(`❌ Token metadata not found:`, { token0: poolMeta.token0, token1: poolMeta.token1 });
                            } else {
                              // Get token prices - for slipstream pools, we need both token prices
                              let token0Price = 0;
                              let token1Price = 0;

                              // Determine which token is which and get appropriate prices
                              if (poolMeta.token0.toLowerCase() === WETH_ADDRESS.toLowerCase()) {
                                token0Price = wethPrice;
                                token1Price = 1.0; // USDC is always $1
                              } else if (poolMeta.token1.toLowerCase() === WETH_ADDRESS.toLowerCase()) {
                                token0Price = 1.0; // USDC is always $1
                                token1Price = wethPrice;
                              } else {
                                // For other token pairs, we'd need to fetch their prices
                                console.warn(`⚠️ Non-WETH/USDC slipstream pool detected: ${pool.name}`);
                                token0Price = 1.0; // Fallback
                                token1Price = 1.0; // Fallback
                              }

                              if (import.meta.env.DEV) console.log(`🔍 Calculating NFT position value for ${tokenId}:`, {
                                liquidity: positionDetails.liquidity,
                                tickLower: positionDetails.tickLower,
                                tickUpper: positionDetails.tickUpper,
                                currentTick,
                                token0Price,
                                token1Price,
                                token0Decimals: token0Meta.decimals,
                                token1Decimals: token1Meta.decimals
                              });

                              const positionData = calculateNftPositionValueUsd({
                                liquidity: positionDetails.liquidity,
                                tickLower: positionDetails.tickLower,
                                tickUpper: positionDetails.tickUpper,
                                currentTick,
                                token0Decimals: token0Meta.decimals,
                                token1Decimals: token1Meta.decimals,
                                token0PriceUsd: token0Price,
                                token1PriceUsd: token1Price,
                              });

                              // Use simpler calculation: identify WETH and calculate deposit value
                              const isToken0Weth = token0Meta.symbol === 'WETH';
                              const wethAmount = isToken0Weth ? positionData.amount0 : positionData.amount1;
                              const usdcAmount = isToken0Weth ? positionData.amount1 : positionData.amount0;
                              const wethPriceForCalculation = isToken0Weth ? token0Price : token1Price;

                              // Simple calculation: WETH amount * WETH price + USDC amount (USDC ≈ $1)
                              positionValueUsd = wethAmount * wethPriceForCalculation + usdcAmount;

                              if (import.meta.env.DEV) console.log(`💰 Position token amounts:`, {
                                wethAmount: wethAmount.toFixed(6),
                                usdcAmount: usdcAmount.toFixed(2),
                                wethPrice: wethPriceForCalculation.toFixed(2),
                                calculatedValue: positionValueUsd.toFixed(2),
                                originalValue: positionData.valueUsd.toFixed(2)
                              });
                            }
                          }
                        } else {
                          if (import.meta.env.DEV) console.log(`⚠️ Cannot calculate position value - missing data:`, {
                            hasPositionDetails: !!positionDetails,
                            wethPrice,
                            liquidity: positionDetails?.liquidity
                          });
                        }

                        // Calculate position-specific APR for slipstream using liquidity-based formula
                        let aprValue = 0;
                        if (positionDetails && currentTick !== null && emissions && gaugeLiquidity) {
                          // 1. Check if position is in range
                          const isInRange = currentTick >= positionDetails.tickLower && currentTick <= positionDetails.tickUpper;

                          if (!isInRange) {
                            // Out of range = no APR earned
                            aprValue = 0;
                            if (import.meta.env.DEV) console.log(`📊 Position OUT OF RANGE - APR: 0%`);
                          } else if (positionValueUsd > 0 && aeroPrice > 0) {
                            // 2. Calculate liquidity share
                            const positionLiquidity = BigInt(positionDetails.liquidity);
                            const totalGaugeLiquidity = BigInt(gaugeLiquidity);

                            if (totalGaugeLiquidity > 0n) {
                              // 3. Calculate annual AERO rewards for this position
                              // emissions is in wei per second
                              const emissionsPerSecond = Number(emissions) / 1e18; // Convert wei to AERO
                              const secondsPerYear = 31536000;
                              const liquidityShare = Number(positionLiquidity) / Number(totalGaugeLiquidity);

                              // Position's annual AERO rewards
                              const positionAnnualAero = emissionsPerSecond * liquidityShare * secondsPerYear;

                              // Position's annual AERO rewards in USD
                              const positionAnnualRewardsUsd = positionAnnualAero * aeroPrice;

                              // APR = (annual rewards USD / position value USD) * 100
                              aprValue = (positionAnnualRewardsUsd / positionValueUsd) * 100;

                              if (import.meta.env.DEV) console.log(`📊 Position APR calculation (liquidity-based):`, {
                                isInRange: true,
                                positionLiquidity: positionDetails.liquidity,
                                totalGaugeLiquidity: gaugeLiquidity,
                                liquidityShare: (liquidityShare * 100).toFixed(6) + '%',
                                emissionsPerSecond: emissionsPerSecond.toFixed(6) + ' AERO/sec',
                                positionAnnualAero: positionAnnualAero.toFixed(2) + ' AERO/year',
                                aeroPrice: aeroPrice.toFixed(4),
                                positionAnnualRewardsUsd: positionAnnualRewardsUsd.toFixed(2),
                                positionValueUsd: positionValueUsd.toFixed(2),
                                calculatedPositionAPR: aprValue.toFixed(2) + '%'
                              });
                            } else {
                              if (import.meta.env.DEV) console.log(`⚠️ Total gauge liquidity is 0 - cannot calculate APR`);
                              aprValue = 0;
                            }
                          } else {
                            if (import.meta.env.DEV) console.log(`⚠️ Missing data: positionValueUsd=${positionValueUsd}, aeroPrice=${aeroPrice}`);
                            aprValue = 0;
                          }
                        } else {
                          // Fallback to pool APR if we can't calculate liquidity-based APR
                          aprValue = poolMetrics.aprCurrentPct || 0;
                          if (import.meta.env.DEV) console.log(`📊 Using pool APR (missing data): ${aprValue}%`);
                        }

                        const position: UserPosition = {
                          poolName: `${pool.name} #${tokenId}`,
                          poolType: 'slipstream',
                          poolAddress: pool.pool,
                          lpBalance: 0, // NFTs don't have LP balance
                          stakedBalance: 1, // Each NFT represents 1 staked position
                          positionValueUsd: positionValueUsd,
                          aprCurrent: aprValue,
                          claimableEmissions,
                          tokenId: tokenId,
                          // Enhanced fields for slipstream positions
                          priceRange: priceRange,
                          tokenPrice: wethPrice,
                          isInRange: isInRangeFlag,
                          feeTier: feeTier,
                          aeroPrice: aeroPrice,
                        };

                        // Add NFT details to debug data
                        const debugPosition = {
                          ...position,
                          positionDetails,
                          priceRange,
                          isInRange: isInRangeFlag,
                          feeTier
                        };

                        if (import.meta.env.DEV) console.log(`✅ Created NFT position for ${pool.name} #${tokenId}:`, debugPosition);
                        return debugPosition;
                      }));

                      return nftPositions;
            } else {
              if (import.meta.env.DEV) console.log(`❌ No NFT positions found in ${pool.name}`);
              return [];
            }
          } catch (nftErr) {
            const gaugeError = nftErr instanceof Error ? nftErr.message : String(nftErr);
            if (import.meta.env.DEV) console.log(`⚠️ NFT position query failed for ${pool.name}:`, gaugeError);

            // Collect debug data for failed pool
            const debugPosition = {
              poolName: pool.name,
              poolAddress: pool.pool,
              gaugeAddress: pool.gauge,
              nftPositions: [],
              totalNfts: 0,
              hasPosition: false,
              errors: [gaugeError],
            };
            debugUserPositions.push(debugPosition);

            return [];
          }
        } else {
          // Basic pools - existing logic
          let gaugeData = null;
          let stakedBalance = 0;
          let claimableEmissions = 0;
          let gaugeError = '';
          let lpBalance = 0;
          let lpError = '';

          // Try to get gauge data for basic pools
          try {
            gaugeData = await fetchUserGaugeData(pool.gauge, walletAddr);
            stakedBalance = formatFromWei(gaugeData.balanceOf, 18);
            claimableEmissions = formatFromWei(gaugeData.earned, 18);
            if (import.meta.env.DEV) console.log(`📊 Gauge data for ${pool.name}: staked=${stakedBalance}, earned=${claimableEmissions}`);
          } catch (gaugeErr) {
            gaugeError = gaugeErr instanceof Error ? gaugeErr.message : String(gaugeErr);
            if (import.meta.env.DEV) console.log(`⚠️ Gauge query failed for ${pool.name}:`, gaugeError);
            // Continue without gauge data
          }

          // Check for unstaked LP tokens in wallet (basic pools only)
          try {
            const lpBalanceData = await fetchUserTokenBalance(pool.pool, walletAddr);
            lpBalance = formatFromWei(lpBalanceData.balance, 18);
            if (import.meta.env.DEV) console.log(`📊 LP balance for ${pool.name}: ${lpBalance}`);
          } catch (lpErr) {
            lpError = lpErr instanceof Error ? lpErr.message : String(lpErr);
            if (import.meta.env.DEV) console.log(`⚠️ LP balance query failed for ${pool.name}:`, lpErr);
            // Continue without LP balance
          }

          // Collect debug data for this pool
          const debugPosition = {
            poolName: pool.name,
            poolAddress: pool.pool,
            gaugeAddress: pool.gauge,
            lpBalanceRaw: gaugeData?.balanceOf || '0',
            lpBalanceFormatted: lpBalance,
            stakedBalanceRaw: gaugeData?.balanceOf || '0',
            stakedBalanceFormatted: stakedBalance,
            claimableEmissionsRaw: gaugeData?.earned || '0',
            claimableEmissionsFormatted: claimableEmissions,
            hasPosition: stakedBalance > 0 || lpBalance > 0,
            errors: [gaugeError, lpError].filter(Boolean),
          };
          debugUserPositions.push(debugPosition);

          // Show position if user has either staked tokens OR unstaked LP tokens
          if (stakedBalance > 0 || lpBalance > 0) {
            if (import.meta.env.DEV) console.log(`✅ Found position in ${pool.name}: staked=${stakedBalance}, lp=${lpBalance}`);

            // Fetch pool metrics and total supply with enhanced error handling for debugging
            // Note: totalSupply is only used for basic pools (slipstream pools don't implement it)
            if (import.meta.env.DEV) {
              console.log(`🔍 About to fetch data for ${pool.name}:`, {
                poolAddress: pool.pool,
                poolKind: pool.kind,
                poolName: pool.name
              });
            }

            // Only fetch totalSupply for basic pools (slipstream pools don't implement totalSupply)
            // Use Promise.allSettled to handle individual failures gracefully
            const totalSupplyPromise = pool.kind === 'basic'
              ? fetchTokenTotalSupply(pool.pool, { bypassCache })
              : Promise.resolve({ totalSupply: '0' }); // Skip for slipstream pools

            const [poolMetricsResult, totalSupplyResult] = await Promise.allSettled([
              fetchPoolMetrics(pool.pool, { bypassCache }),
              totalSupplyPromise
            ]);

            // Extract results or use fallback values
            let poolMetrics: any = null;
            let totalSupplyData: any = null;

            if (poolMetricsResult.status === 'fulfilled') {
              poolMetrics = poolMetricsResult.value;
            } else {
              console.error(`❌ fetchPoolMetrics failed for ${pool.name} (${pool.pool}):`, {
                error: poolMetricsResult.reason,
                errorMessage: poolMetricsResult.reason instanceof Error ? poolMetricsResult.reason.message : String(poolMetricsResult.reason),
                poolAddress: pool.pool,
                poolKind: pool.kind
              });
              // Use fallback values
              poolMetrics = { tvlUsd: 0, stakedUsd: 0, aprCurrent: 0, aprCurrentPct: 0 };
            }

            if (totalSupplyResult.status === 'fulfilled') {
              totalSupplyData = totalSupplyResult.value;
            } else {
              console.error(`❌ fetchTokenTotalSupply failed for ${pool.name} (${pool.pool}):`, {
                error: totalSupplyResult.reason,
                errorMessage: totalSupplyResult.reason instanceof Error ? totalSupplyResult.reason.message : String(totalSupplyResult.reason),
                poolAddress: pool.pool,
                poolKind: pool.kind
              });
              // Use fallback - will need to calculate from reserves or use 0
              totalSupplyData = { totalSupply: '0' };
            }

            if (import.meta.env.DEV) {
              console.log(`📊 Pool metrics for ${pool.name}:`, poolMetrics);
              console.log(`📊 Total supply for ${pool.name}:`, totalSupplyData);
            }

            // Debug APR value for basic pools
            const aprValue = poolMetrics.aprCurrentPct || 0;
            if (import.meta.env.DEV) console.log(`📊 APR for ${pool.name}: ${aprValue}% (from poolMetrics.aprCurrentPct)`);

            // Calculate position value - use the actual balance that exists
            const totalPositionBalance = stakedBalance > 0 ? stakedBalance : lpBalance;
            const totalLpSupply = formatFromWei(totalSupplyData.totalSupply, 18);

            if (import.meta.env.DEV) console.log(`🔍 Basic pool calculation inputs:`, {
              totalPositionBalance,
              poolTvlUsd: poolMetrics.tvlUsd,
              totalLpSupply,
              poolName: pool.name
            });

            // Validate inputs before calculation
            if (totalPositionBalance <= 0) {
              console.error(`❌ Invalid position balance for ${pool.name}: ${totalPositionBalance}`);
            }
            if (poolMetrics.tvlUsd <= 0) {
              console.error(`❌ Invalid TVL for ${pool.name}: ${poolMetrics.tvlUsd}`);
            }
            if (totalLpSupply <= 0) {
              console.error(`❌ Invalid total supply for ${pool.name}: ${totalLpSupply}`);
            }

            const positionValueUsd = calculatePositionValueUsd(
              totalPositionBalance,
              poolMetrics.tvlUsd,
              totalLpSupply
            );

            if (import.meta.env.DEV) console.log(`💰 Calculated basic position value: $${positionValueUsd.toFixed(2)}`);

            if (import.meta.env.DEV) console.log(`🔍 Creating basic position for ${pool.name} with AERO price: $${aeroPrice.toFixed(4)}`);

            const position: UserPosition = {
              poolName: pool.name,
              poolType: pool.kind,
              poolAddress: pool.pool,
              lpBalance,
              stakedBalance,
              positionValueUsd,
              aprCurrent: aprValue,
              claimableEmissions,
              // Enhanced fields for basic positions
              priceRange: "$0 to ∞",
              tokenPrice: aeroPrice,
              isInRange: true, // Basic pools are always in range
              aeroPrice: aeroPrice,
            };

            if (import.meta.env.DEV) console.log(`✅ Created position for ${pool.name}:`, position);
            return [position];
          } else {
            if (import.meta.env.DEV) console.log(`❌ No position found in ${pool.name}`);
            return [];
          }
        }
      } catch (err) {
        console.error(`❌ Failed to fetch position data for pool ${pool.name}:`, err);
        console.error(`❌ Pool details:`, { name: pool.name, pool: pool.pool, gauge: pool.gauge });
        return [];
      }
    });

    const positionArrays = await Promise.allSettled(positionPromises);
    const positions: UserPosition[] = [];

    positionArrays.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        positions.push(...result.value);
      } else {
        console.error(`❌ Failed to process pool ${index}:`, result.reason);
      }
    });

    if (import.meta.env.DEV) console.log(`✅ Found ${positions.length} positions:`, positions);

    // Group positions by type
    positions.forEach(position => {
      const groupIndex = position.poolType === 'slipstream' ? 0 : 1;
      groups[groupIndex].positions.push(position);
    });

    if (import.meta.env.DEV) console.log(`📋 Final groups:`, groups);

    return groups;
  }, [poolEntries]);

  const loadData = useCallback(async (isRefresh = false) => {
    if (!walletAddress) {
      setPositionGroups([]);
      setLoading(false);
      return;
    }

    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const groups = await processUserPositions(walletAddress, isRefresh);
      setPositionGroups(groups);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user positions';
      setError(errorMessage);
      console.error('Error loading user positions:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [walletAddress, processUserPositions]);

  const refreshCurrentData = useCallback(async (): Promise<UserPositionGroup[]> => {
    if (!walletAddress) {
      return [];
    }

    try {
      setRefreshing(true);
      setError(null);

      const groups = await processUserPositions(walletAddress, true); // bypassCache=true for manual refresh
      setPositionGroups(groups);
      return groups; // Return fresh data immediately
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user positions';
      setError(errorMessage);
      console.error('Error loading user positions:', err);
      throw err; // Re-throw so caller knows it failed
    } finally {
      setRefreshing(false);
    }
  }, [walletAddress, processUserPositions]);

  // Load data when wallet address changes
  useEffect(() => {
    loadData();
  }, [loadData]);

  return {
    positionGroups,
    loading,
    error,
    refreshing,
    refreshCurrentData
  };
}
